import { Component, computed, effect, inject, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DurationPipe } from '@core/pipes/duration.pipe';
import { NumberLocalePipe } from '@core/pipes/number-locale.pipe';
import { NumberSuffixPipe } from '@core/pipes/number-suffix.pipe';
import { EMatchingType } from '@enums/campaign-analytics.enum';
import { ICtaResultApi } from '@interfaces/campaign-analytics-api.interface';
import { IAnalyticsNode, ICtaResult } from '@interfaces/campaign-analytics.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  EDetailLevel,
  ICampaignExportApi,
} from '@pages/campaigns/campaign-details/campaign-details-banner/campaign-export-dialog/campaign-export-dialog.interface';
import { IconComponent } from '@shared/components/icon/icon.component';
import { isNil, maxBy } from 'lodash';
import { ICtaAnalyticsData } from '../../cta-analytics-table.interface';
import { ISponsoredKpiCard } from './cta-analytics-details-deliverables-header.interface';
import { ManualMatchingStatusListStore } from './manual-cta-result-matching-type/manual-cta-result-matching-type-list.store';
import { ManualCtaResultMatchingTypeComponent } from './manual-cta-result-matching-type/manual-cta-result-matching-type.component';
import {
  IManualMatchingStatus,
  IManualMatchingStatusContext,
} from './manual-cta-result-matching-type/manual-cta-result-matching-type.interface';
import { ManualMatchingStatusService } from './manual-cta-result-matching-type/manual-cta-result-matching-type.service';

@Component({
  imports: [
    DurationPipe,
    IconComponent,
    ManualCtaResultMatchingTypeComponent,
    MatButtonModule,
    MatTooltipModule,
    NumberLocalePipe,
    NumberSuffixPipe,
    TranslateModule,
  ],
  selector: 'app-cta-analytics-details-deliverables-header',
  templateUrl: './cta-analytics-details-deliverables-header.component.html',
})
export class CtaAnalyticsDetailsDeliverablesHeaderComponent {
  public readonly analyticsNode = input.required<IAnalyticsNode>();
  public readonly ctaResult = input.required<ICtaResult>();
  public readonly execution = input.required<ICtaAnalyticsData>();

  public readonly creatorBriefingMode = input<boolean>(false);
  public readonly exportMode = input<boolean>(false);
  public readonly campaignExport = input<ICampaignExportApi | null>(null);
  public readonly adminMode = input<boolean>(false);

  public readonly ctaShowRequested = output<string>();

  public readonly allCtas = computed((): ICtaResultApi[] =>
    Array.from(this.analyticsNode().content?.ctas?.values() ?? []),
  );

  public readonly bestMatchedCtaId = computed((): string | null => {
    const matched: ICtaResultApi[] = this.allCtas().filter((c) => c.matchingType === EMatchingType.matched);
    const best: ICtaResultApi | undefined = maxBy(
      matched,
      (c) => c.ctaAnalytics?.matchingScore ?? Number.NEGATIVE_INFINITY,
    );

    return best?.ctaId ?? null;
  });

  public readonly hasSponsoredContentRow = computed((): boolean => {
    const ex: ICtaAnalyticsData = this.execution();

    return ex.status !== EMatchingType.planned && ex.details.sponsoredContentStartDate !== undefined;
  });

  public readonly otherCtas = computed((): ICtaResultApi[] => {
    const selectedId: string = this.ctaResult().ctaId;

    return this.allCtas()
      .filter((c) => c.ctaId !== selectedId)
      .sort((a, b) => a.name.localeCompare(b.name));
  });

  public readonly showSeeAlsoSection = computed((): boolean => this.otherCtas().length > 0);

  public readonly showSponsoredTiming = computed((): boolean => this.hasSponsoredContentRow());

  public readonly showTimingSection = computed((): boolean => {
    const isMatched = this.execution().status === EMatchingType.matched;

    return isMatched || this.hasSponsoredContentRow();
  });

  public readonly showSponsoredKpis = computed((): boolean => {
    const sponsoredTimeKpi = this.execution()?.ctaAnalytics?.sponsoredTimeKpi;
    const hasTotalWatchedHours = !isNil(sponsoredTimeKpi?.totalWatchedHours);
    const hasAvgViewers = !isNil(sponsoredTimeKpi?.avgViewers);
    const hasPeakViewers = !isNil(sponsoredTimeKpi?.peakViewers);

    return !!sponsoredTimeKpi && (hasTotalWatchedHours || hasAvgViewers || hasPeakViewers);
  });

  public readonly sponsoredKpis = computed(() => {
    const sponsoredTimeKpi = this.execution()?.ctaAnalytics?.sponsoredTimeKpi;

    if (!sponsoredTimeKpi) {
      return [];
    }

    const kpis: ISponsoredKpiCard[] = [];

    if (!isNil(sponsoredTimeKpi.avgViewers)) {
      kpis.push({
        icon: '/icons/view.svg',
        label: this.translate.instant('pages.campaignDetails.kpis.avgViewers'),
        value: sponsoredTimeKpi.avgViewers,
      });
    }

    if (!isNil(sponsoredTimeKpi.peakViewers)) {
      kpis.push({
        icon: '/icons/view.svg',
        label: this.translate.instant('pages.campaignDetails.kpis.peakViewers'),
        value: sponsoredTimeKpi.peakViewers,
      });
    }

    if (!isNil(sponsoredTimeKpi.totalWatchedHours)) {
      kpis.push({
        icon: '/icons/clock.svg',
        label: this.translate.instant('pages.campaignDetails.kpis.totalWatchedHours'),
        value: sponsoredTimeKpi.totalWatchedHours,
      });
    }

    return kpis.sort((a, b) => a.label.localeCompare(b.label));
  });

  /** True when (campaignStatus === live || campaignProcessStatus === processing) && socialContentId is set. */
  public readonly campaignIsLive = computed((): boolean => {
    const ex = this.execution();

    return this.manualMatchingStatusService.computeCampaignIsLive(
      ex.campaignStatus,
      ex.campaignProcessStatus,
      ex.socialContentId,
    );
  });

  /** Edit button visible: not admin, not briefing, correct export level, campaign live and processing. */
  public readonly matchingActionsVisible = computed((): boolean =>
    this.manualMatchingStatusService.computeActionsVisible({
      adminMode: this.adminMode(),
      campaignExport: this.campaignExport(),
      campaignIsLive: this.campaignIsLive(),
      campaignProcessStatus: this.execution().campaignProcessStatus,
      creatorBriefingMode: this.creatorBriefingMode(),
      exportMode: this.exportMode(),
    }),
  );

  /** Subheader line visible: not briefing, and not hidden by export settings. */
  public readonly matchingSubheaderVisible = computed((): boolean =>
    this.manualMatchingStatusService.computeSubheaderVisible({
      campaignExport: this.campaignExport(),
      creatorBriefingMode: this.creatorBriefingMode(),
      exportMode: this.exportMode(),
    }),
  );

  /** Context passed to every manual-cta-result-matching-type instance in this panel. */
  public readonly matchingStatusContext = computed((): IManualMatchingStatusContext => {
    const ex = this.execution();

    return {
      campaignId: ex.campaignId,
      creatorId: ex.creatorId,
      socialContentId: ex.socialContentId,
    };
  });

  /**
   * True when statuses should be fetched for the current context.
   * Also used to gate manualMatchingStatuses so in-flight responses that
   * arrive after the condition flips are never surfaced to the template.
   */
  private readonly canFetchStatuses = computed((): boolean => {
    const ex = this.execution();

    return (
      !!ex.socialContentId &&
      this.allCtas().length > 0 &&
      !this.adminMode() &&
      !this.creatorBriefingMode() &&
      (!this.exportMode() ||
        (!!this.campaignExport()?.showDeliverableStatus &&
          this.campaignExport()?.detailLevel === EDetailLevel.detailedAnalytics))
    );
  });

  /** Map of ctaId → IManualMatchingStatus, null while loading. Returns settled empty map when fetch is suppressed. */
  public readonly manualMatchingStatuses = computed((): Map<string, IManualMatchingStatus> | null => {
    if (!this.canFetchStatuses()) {
      return new Map();
    }

    return this.manualMatchingStatusListStore.statuses$();
  });

  public readonly EMatchingType = EMatchingType;

  private readonly translate = inject(TranslateService);
  private readonly manualMatchingStatusListStore = inject(ManualMatchingStatusListStore);
  private readonly manualMatchingStatusService = inject(ManualMatchingStatusService);

  constructor() {
    effect(() => {
      if (!this.canFetchStatuses()) {
        this.manualMatchingStatusListStore.clearStatuses();

        return;
      }

      const ex = this.execution();

      this.manualMatchingStatusListStore.loadStatuses({
        campaignExportId: this.exportMode() ? (this.campaignExport()?.id ?? null) : null,
        campaignId: ex.campaignId,
        creatorId: ex.creatorId,
        ctaTemplateIds: this.allCtas().map((c) => c.ctaId),
        socialContentId: ex.socialContentId!,
      });
    });
  }

  public getMatchingLabelKey(cta: ICtaResult | ICtaResultApi): string {
    return this.manualMatchingStatusService.computeMatchingLabelKey(cta, this.bestMatchedCtaId());
  }

  public getManualStatusForCta(ctaId: string): IManualMatchingStatus | null | undefined {
    const statuses = this.manualMatchingStatuses();

    return statuses ? (statuses.get(ctaId) ?? null) : undefined;
  }

  public onShowClick(ctaId: string): void {
    this.ctaShowRequested.emit(ctaId);
  }
}
